import { type AdminOrderDestination, formatSizeBreakdown, sizeTotalsOf } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatCount } from '@/shared/lib';
import { cn } from '@/shared/ui';

interface DestinationsTableProps {
  readonly destinations: readonly AdminOrderDestination[];
}

const CELL_CLASS = 'border border-foreground/20 px-3 py-2 align-top print:border-black';

/** 발주서 납품처 표 — 배송지마다 한 줄, 사이즈가 있으면 내역과 합계를 함께 보인다. */
export const DestinationsTable = ({ destinations }: DestinationsTableProps): ReactElement => {
  const hasSizes = destinations.some((destination) => destination.sizes.length > 0);
  return (
    <>
      <table aria-label="납품처" className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th scope="col" className={cn(CELL_CLASS, 'text-left')}>
              주소
            </th>
            <th scope="col" className={cn(CELL_CLASS, 'text-left')}>
              수령인
            </th>
            <th scope="col" className={cn(CELL_CLASS, 'text-left')}>
              연락처
            </th>
            <th scope="col" className={cn(CELL_CLASS, 'text-right')}>
              수량
            </th>
            {hasSizes ? (
              <th scope="col" className={cn(CELL_CLASS, 'text-left')}>
                사이즈
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {destinations.map((destination) => {
            const { postalCode, address1, address2, recipientName, recipientPhone } =
              destination.shippingAddress;
            return (
              <tr key={destination.id}>
                <td className={CELL_CLASS}>{[postalCode, address1, address2].join(' ').trim()}</td>
                <td className={CELL_CLASS}>{recipientName}</td>
                <td className={CELL_CLASS}>{recipientPhone}</td>
                <td className={cn(CELL_CLASS, 'text-right whitespace-nowrap tabular-nums')}>
                  {formatCount(destination.quantity)}
                </td>
                {hasSizes ? (
                  <td className={CELL_CLASS}>{formatSizeBreakdown(destination.sizes)}</td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {hasSizes ? (
        <p role="note" aria-label="사이즈별 합계" className="mt-2 text-sm">
          사이즈별 합계: {formatSizeBreakdown(sizeTotalsOf(destinations))}
        </p>
      ) : null}
    </>
  );
};
